import React, { useState, useEffect, useRef } from 'react';
import './ApplyForms.css';
import './AdminDashboard.css';
import { getT, formatT } from '../i18n';
import {
  canDepartmentReviewApplicant,
  canDepartmentSeeApplication,
  getApproveTargetStatus,
  getCurrentStageReviewer,
  getDepartmentKey,
  getPipelineWaitingMessage,
  getRequiredStatusForDepartment,
  isSameReviewer,
  isReviewQueueApplicant,
  isSharedQueueDepartment,
  usesInterviewerAssignment,
  getDepartmentKeyForStatus,
  getStageReviewer,
  PIPELINE_STATUS
} from '../utils/reviewPipeline';
import { filterSubmittedJobApplications } from '../utils/applicationFilters';
import {
  isApplicationAssignedToUser,
  isDepartmentInterviewerUser,
} from '../utils/assignmentHelpers';
import { openDigitalResumeTab, openFullDetailsTab } from '../utils/applicantDocumentViewer';
import SidebarNavIcon from './SidebarNavIcons';
import MessagesFloatButton from './MessagesFloatButton';
import hLogoImg from '../HLOGO.png';

const StaffDashboard = ({ currentUser, lang = 'zh', onLangChange, applications, assignments, fetchApplications, onFetchAssignments, handleUpdateStatus, onClaimReview, notesInputs, handleNoteChange, messages = [], onReplyMessage, onDeleteMessage }) => {
  const t = getT(lang);
  const ts = t.staff;
  const tc = t.common;
  const [activeTab, setActiveTab] = useState('app-review');
  const [notifications, setNotifications] = useState([]);
  const [showNotifDropdown, setShowNotifDropdown] = useState(false);
  const prevVisibleAppsRef = useRef([]);
  const [userPosition, setUserPosition] = useState(currentUser?.position || '');
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  const role = currentUser?.role || 'Staff';

  const [replyInputs, setReplyInputs] = useState('');
  const [selectedMessageId, setSelectedMessageId] = useState(messages[0]?.id || null);

  const handleReplyChange = (value) => {
    setReplyInputs(value);
  };

  const handleSubmitReply = async () => {
    const text = replyInputs.trim();
    if (!text || !selectedMessageId) return;
    if (typeof onReplyMessage === 'function') {
      const sent = await onReplyMessage(selectedMessageId, text);
      if (sent !== false) setReplyInputs('');
    }
  };

  const handleDeleteSelectedMessage = () => {
    if (!selectedMessageId || typeof onDeleteMessage !== 'function') return;
    onDeleteMessage(selectedMessageId);
  };

  useEffect(() => {
    if (!messages || messages.length === 0) {
      setSelectedMessageId(null);
      return;
    }
    if (!messages.find((m) => m.id === selectedMessageId)) {
      setSelectedMessageId(messages[0].id);
    }
  }, [messages, selectedMessageId]);

  const departmentKey = getDepartmentKey({ ...currentUser, position: userPosition || currentUser?.position });
  const pipelineLabels = {
    hr: t.admin.pipelineHr,
    operations: t.admin.pipelineOperations,
    teamLeader: t.admin.pipelineTeamLeader,
    client: t.admin.pipelineClient,
    headAdmin: t.admin.pipelineHeadAdmin,
    waitingFor: t.admin.pipelineWaitingFor,
    failedInterview: t.admin.pipelineFailedInterview,
    alreadyPassed: t.admin.pipelineAlreadyPassed,
    notYourTurn: t.admin.pipelineNotYourTurn
  };

  const isVisibleToCurrentUser = (app) => (
    canDepartmentSeeApplication(app, currentUser)
  );

  const isAtMyReviewStage = (app) => {
    const requiredStatus = getRequiredStatusForDepartment(departmentKey);
    return requiredStatus ? app.status === requiredStatus : false;
  };

  const jobApplications = filterSubmittedJobApplications(applications || []);

  const visibleApplications = jobApplications.filter((app) => {
    if (!isVisibleToCurrentUser(app) || !isReviewQueueApplicant(app)) return false;
    if (isDepartmentInterviewerUser(currentUser)) {
      if (isSharedQueueDepartment(departmentKey)) return true;
      return isApplicationAssignedToUser(assignments, app.id, currentUser);
    }
    return true;
  });

  useEffect(() => {
    if (typeof onFetchAssignments === 'function') {
      onFetchAssignments();
    }
  }, [onFetchAssignments]);

  const myStats = jobApplications.reduce((acc, app) => {
    if (visibleApplications.some((visibleApp) => visibleApp.id === app.id)) {
      acc.assigned += 1;
      if (isAtMyReviewStage(app)) acc.pendingAction += 1;
    }
    return acc;
  }, { assigned: 0, pendingAction: 0 });

  const openStaffApplicantReview = (app) => {
    if (typeof onClaimReview === 'function' && usesInterviewerAssignment(departmentKey)) {
      onClaimReview(app.id);
    }
    openFullDetailsTab(app, {
      labels: {
        reviewDetails: ts.reviewCv,
        sections: t.admin?.sections || {},
        attachments: t.admin?.attachments || { resume: ts.viewResume },
        voiceScore: t.admin?.voiceScore,
        noAssessmentYet: t.admin?.noAssessmentYet,
      },
      attachmentDocs: [
        { label: ts.viewResume, keys: ['resume_file', 'resume_files', 'resume'] },
      ],
    });
  };

  const openStaffDigitalResume = (app) => {
    openDigitalResumeTab(app, {
      viewResume: ts.viewResume,
      sections: t.admin?.sections || {},
    });
  };

  // --- NOTIFICATION LOGIC ---
  useEffect(() => {
    if (visibleApplications.length > 0) {
      const newApps = visibleApplications.filter(app => !prevVisibleAppsRef.current.find(prev => prev.id === app.id));
      if (newApps.length > 0) {
        const newNotifs = newApps.map(app => ({
          id: Date.now() + app.id,
          message: formatT(ts.newAssignment, { no: app.application_no || '—', position: app.position }),
          time: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
          read: false
        }));
        setNotifications(prev => [...newNotifs, ...prev]);
      }
    }
    prevVisibleAppsRef.current = visibleApplications;
  }, [visibleApplications, ts.newAssignment]);

  const markAllAsRead = () => setNotifications(prev => prev.map(n => ({ ...n, read: true })));
  const unreadCount = notifications.filter(n => !n.read).length;

  const closeMobileMenu = () => setIsMobileMenuOpen(false);

  const navigateToTab = (tabId) => {
    setActiveTab(tabId);
    closeMobileMenu();
  };

  const displayName = currentUser?.full_name || currentUser?.name || 'Staff';
  const userInitial = (displayName || 'S').charAt(0).toUpperCase();

  useEffect(() => {
    closeMobileMenu();
  }, [activeTab]);

  useEffect(() => {
    if (!isMobileMenuOpen) return undefined;
    const handleEscape = (event) => {
      if (event.key === 'Escape') closeMobileMenu();
    };
    window.addEventListener('keydown', handleEscape);
    return () => window.removeEventListener('keydown', handleEscape);
  }, [isMobileMenuOpen]);

  const renderStaffProfile = (className = '') => (
    <div className={`user-mini-profile${className ? ` ${className}` : ''}`}>
      <div className="avatar">{userInitial}</div>
      <div className="info">
        <p className="name">{displayName}</p>
        <p className="role">{userPosition || role}</p>
      </div>
    </div>
  );

  return (
    <div className="crm-dashboard-layout">
      {/* SIMPLIFIED SIDEBAR */}
      <aside className="crm-sidebar">
        <div className="sidebar-brand" style={{ marginBottom: '40px' }}>
          <h2 style={{ color: '#fff', margin: '0 0 5px 0' }}>{ts.portal}</h2>
          <span className="role-badge" style={{ background: '#00D27F', color: '#000', padding: '3px 8px', borderRadius: '4px', fontSize: '0.7rem', fontWeight: 'bold' }}>{role.toUpperCase()}</span>
        </div>

        <nav className="sidebar-menu">
          <div className="menu-group-label">{ts.recruitment}</div>
          <button type="button" className={`sidebar-menu-item menu-item ${activeTab === 'app-review' ? 'active' : ''}`} onClick={() => navigateToTab('app-review')}>
            <SidebarNavIcon id="app-review" />
            <span className="menu-item-label">{ts.appReview}</span>
          </button>
          <button type="button" className={`sidebar-menu-item menu-item ${activeTab === 'messages' ? 'active' : ''}`} onClick={() => navigateToTab('messages')}>
            <SidebarNavIcon id="messages" />
            <span className="menu-item-label">{tc.messages}</span>
          </button>
          
          <div style={{ padding: '10px 15px', marginTop: '10px', background: 'rgba(0, 210, 127, 0.05)', borderRadius: '8px', border: '1px solid rgba(0, 210, 127, 0.1)' }}>
            <p style={{ margin: 0, fontSize: '0.7rem', color: '#8090a0', textTransform: 'uppercase' }}>{ts.currentTask}</p>
            <p style={{ margin: '4px 0 0 0', fontSize: '0.85rem', color: '#00D27F', fontWeight: 'bold' }}>{myStats.pendingAction} {ts.pendingReviews}</p>
          </div>

          <div className="menu-group-label" style={{ marginTop: '20px' }}>{t.sidebar.settings}</div>
          <button type="button" className={`sidebar-menu-item menu-item ${activeTab === 'profile' ? 'active' : ''}`} onClick={() => navigateToTab('profile')}>
            <SidebarNavIcon id="profile" />
            <span className="menu-item-label">{ts.myProfile}</span>
          </button>

          {/* NOTIF BELL */}
          <div style={{ marginTop: '20px', position: 'relative' }}>
            <button onClick={() => { setShowNotifDropdown(!showNotifDropdown); markAllAsRead(); }} className="sidebar-menu-item menu-item" style={{ border: 'none', background: 'none', width: '100%' }}>
              <SidebarNavIcon id="notifications" />
              <span className="menu-item-label">
                {ts.notifications}
                {unreadCount > 0 && (
                  <span className="notif-badge" style={{ background: 'red', color: 'white', padding: '2px 6px', borderRadius: '50%', fontSize: '10px', marginLeft: '5px' }}>{unreadCount}</span>
                )}
              </span>
            </button>
            {showNotifDropdown && (
              <div className="notif-dropdown" style={{ position: 'absolute', left: '10px', bottom: '50px', background: '#1a1d2b', border: '1px solid #30363d', borderRadius: '8px', width: '220px', zIndex: 100 }}>
                {notifications.length === 0 ? <div style={{ padding: '10px', fontSize: '0.8rem' }}>{ts.noAlerts}</div> : notifications.map(n => (
                  <div key={n.id} style={{ padding: '10px', borderBottom: '1px solid #333', fontSize: '0.75rem' }}>{n.message}</div>
                ))}
              </div>
            )}
          </div>
        </nav>

        <div className="sidebar-footer" style={{ marginTop: 'auto' }}>
          <div className="user-mini-profile">
            <div className="avatar">{(currentUser?.name || 'U')[0]}</div>
            <div className="info">
              <p className="name">{currentUser?.name || currentUser?.full_name}</p>
              <p className="role" style={{ color: '#00D27F', fontSize: '0.7rem' }}>{userPosition || role}</p>
            </div>
          </div>
        </div>
      </aside>

      <div
        className={`admin-mobile-menu-backdrop${isMobileMenuOpen ? ' open' : ''}`}
        onClick={closeMobileMenu}
        aria-hidden="true"
      />

      <aside className={`admin-mobile-menu-drawer${isMobileMenuOpen ? ' open' : ''}`} aria-hidden={!isMobileMenuOpen}>
        <div className="admin-mobile-menu-header">
          <img src={hLogoImg} alt="Helport" className="admin-mobile-menu-logo" />
          <button type="button" className="admin-mobile-menu-close" onClick={closeMobileMenu} aria-label={tc.close}>
            &times;
          </button>
        </div>

        <nav className="admin-mobile-menu-nav">
          <div className="admin-mobile-menu-group-label">{ts.recruitment}</div>
          <button
            type="button"
            className={`admin-mobile-menu-link${activeTab === 'app-review' ? ' active' : ''}`}
            onClick={() => navigateToTab('app-review')}
          >
            <SidebarNavIcon id="app-review" className="admin-mobile-menu-icon" />
            <span className="admin-mobile-menu-label">{ts.appReview}</span>
          </button>
          <button
            type="button"
            className={`admin-mobile-menu-link${activeTab === 'messages' ? ' active' : ''}`}
            onClick={() => navigateToTab('messages')}
          >
            <SidebarNavIcon id="messages" className="admin-mobile-menu-icon" />
            <span className="admin-mobile-menu-label">{tc.messages}</span>
          </button>

          <div className="staff-mobile-task-card">
            <p className="staff-mobile-task-label">{ts.currentTask}</p>
            <p className="staff-mobile-task-value">{myStats.pendingAction} {ts.pendingReviews}</p>
          </div>

          <div className="admin-mobile-menu-group-label admin-mobile-menu-group-label--spaced">{t.sidebar.settings}</div>
          <button
            type="button"
            className={`admin-mobile-menu-link${activeTab === 'profile' ? ' active' : ''}`}
            onClick={() => navigateToTab('profile')}
          >
            <SidebarNavIcon id="profile" className="admin-mobile-menu-icon" />
            <span className="admin-mobile-menu-label">{ts.myProfile}</span>
          </button>
          <button
            type="button"
            className={`admin-mobile-menu-link${unreadCount > 0 ? ' has-badge' : ''}`}
            onClick={() => { markAllAsRead(); setShowNotifDropdown(false); }}
          >
            <SidebarNavIcon id="notifications" className="admin-mobile-menu-icon" />
            <span className="admin-mobile-menu-label">
              {ts.notifications}{unreadCount > 0 ? ` (${unreadCount})` : ''}
            </span>
          </button>
        </nav>

        {typeof onLangChange === 'function' && (
          <div className="admin-mobile-menu-lang">
            <span className="admin-mobile-menu-lang-label">{tc.language}</span>
            <div className="admin-mobile-menu-lang-options">
              <button type="button" className={lang === 'en' ? 'active' : ''} onClick={() => onLangChange('en')}>{tc.english}</button>
              <button type="button" className={lang === 'zh' ? 'active' : ''} onClick={() => onLangChange('zh')}>{tc.chinese}</button>
            </div>
          </div>
        )}

        <div className="admin-mobile-menu-footer">
          {renderStaffProfile('admin-mobile-menu-profile')}
        </div>
      </aside>

      {activeTab !== 'messages' && (
        <MessagesFloatButton
          count={messages.length}
          onClick={() => navigateToTab('messages')}
          ariaLabel={tc.messages}
        />
      )}

      <main className="crm-main-content">
        <header className="admin-mobile-topbar">
          <img src={hLogoImg} alt="Helport" className="admin-mobile-topbar-logo" />
          <button
            type="button"
            className={`admin-mobile-menu-toggle${isMobileMenuOpen ? ' is-open' : ''}`}
            onClick={() => setIsMobileMenuOpen((open) => !open)}
            aria-label={isMobileMenuOpen ? tc.close : ts.recruitment}
            aria-expanded={isMobileMenuOpen}
          >
            <span />
            <span />
            <span />
          </button>
        </header>
        {activeTab === 'app-review' && (
          <div className="dashboard-container">
            <div className="dashboard-card">
              <h2 className="admin-title">{role} DASHBOARD</h2>
              <div className="section-box monitor-summary">
                <div className="status-summary-grid">
                  <div className="status-card" style={{ borderLeft: '4px solid #00D27F' }}>
                    <p className="status-card-title">{ts.inYourQueue}</p>
                    <p className="status-card-value">{myStats.assigned}</p>
                  </div>
                  <div className="status-card" style={{ borderLeft: '4px solid #ffad1f' }}>
                    <p className="status-card-title">{ts.pendingAction}</p>
                    <p className="status-card-value">{myStats.pendingAction}</p>
                  </div>
                </div>
              </div>

              <div className="section-box">
                <h3 className="admin-apps-heading">{t.admin.appsForReview}</h3>
                {visibleApplications.length === 0 ? (
                  <p className="admin-no-apps">
                    {isDepartmentInterviewerUser(currentUser) ? ts.noAssigned : ts.allClear}
                  </p>
                ) : (
                  <div className="app-list-container">
                    {visibleApplications.map(app => {
                      const stageDeptKey = getDepartmentKeyForStatus(app.status) || departmentKey;
                      const stageReviewer = getStageReviewer(app, stageDeptKey) || getCurrentStageReviewer(app, currentUser);
                      const usesAssignment = usesInterviewerAssignment(departmentKey);
                      const claimedByOther = Boolean(
                        usesAssignment
                        && stageReviewer
                        && isAtMyReviewStage(app)
                        && !isSameReviewer(stageReviewer, currentUser)
                      );
                      const canReview = canDepartmentReviewApplicant(app, currentUser) && !claimedByOther;
                      const approveTarget = getApproveTargetStatus(currentUser);
                      const appStatus = (app.status || '').trim();
                      const isAtClientInterview = appStatus === PIPELINE_STATUS.PENDING_CLIENT;
                      const isAtOperationsInterview = appStatus === PIPELINE_STATUS.PENDING_OPS;
                      const showClientActions = canReview && isAtClientInterview && departmentKey === 'CLIENT';
                      const showOperationsActions = canReview && isAtOperationsInterview && departmentKey === 'OPERATIONS';
                      const showStandardActions = canReview && !showClientActions && !showOperationsActions;

                      return (
                      <div key={app.id} className="app-item-card">
                        <div className="app-item-header">
                          <div>
                            <h4 className="app-item-title">{tc.applicationNo} {app.application_no || '—'} - {app.first_name} {app.last_name}</h4>
                            <span className="app-item-position" style={{ color: '#00D27F' }}>{app.position}</span>
                            {stageReviewer?.name && (
                              <p className="app-item-reviewer">
                                {formatT(ts.interviewedBy, { name: stageReviewer.name })}
                                {stageReviewer.action ? ` · ${stageReviewer.action}` : ''}
                              </p>
                            )}
                          </div>
                          <div className="app-item-actions">
                            {showClientActions && (
                              <button onClick={() => handleUpdateStatus(app.id, PIPELINE_STATUS.PASSED_INTERVIEW)} className="btn-action-approve">{tc.approved}</button>
                            )}
                            {showOperationsActions && (
                              <>
                                <button onClick={() => handleUpdateStatus(app.id, PIPELINE_STATUS.PASSED_INTERVIEW)} className="btn-action-skip">{tc.skip}</button>
                                {approveTarget && (
                                  <button onClick={() => handleUpdateStatus(app.id, approveTarget)} className="btn-action-approve">{tc.approve}</button>
                                )}
                              </>
                            )}
                            {showStandardActions && (
                              <>
                                <button onClick={() => handleUpdateStatus(app.id, 'Onhold')} className="btn-action-onhold">{tc.onhold}</button>
                                {approveTarget && (
                                  <button onClick={() => handleUpdateStatus(app.id, approveTarget)} className="btn-action-approve">{tc.approve}</button>
                                )}
                              </>
                            )}
                            {canReview && (
                              <button onClick={() => handleUpdateStatus(app.id, PIPELINE_STATUS.FAILED_INTERVIEW)} className="btn-action-failed-interview">{tc.failedInterview}</button>
                            )}
                            {!canReview && claimedByOther ? (
                              <span className="pipeline-waiting-note">
                                {formatT(ts.alreadyInterviewedBy, { name: stageReviewer.name })}
                              </span>
                            ) : !canReview ? (
                              <span className="pipeline-waiting-note">
                                {getPipelineWaitingMessage(app, currentUser, pipelineLabels)}
                              </span>
                            ) : null}
                          </div>
                        </div>

                        <div style={{ marginTop: '15px', display: 'flex', flexWrap: 'wrap', gap: '10px' }} className="app-detail-actions">
                          <button
                            type="button"
                            onClick={() => openStaffDigitalResume(app)}
                            className="btn-toggle-details"
                          >
                            {ts.viewResume}
                          </button>
                          <button
                            type="button"
                            onClick={() => openStaffApplicantReview(app)}
                            className="btn-toggle-details"
                            style={{ background: '#28a745' }}
                          >
                            {ts.reviewCv}
                          </button>
                        </div>

                        <div className="app-textarea-wrapper" style={{ marginTop: '15px' }}>
                          <textarea 
                            className="notes-textarea" 
                            placeholder="Add interview notes here..." 
                            value={notesInputs[app.id] || ''} 
                            onChange={(e) => handleNoteChange(app.id, e.target.value)}
                          />
                        </div>
                      </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {activeTab === 'messages' && (
          <div className="tab-container messages-page staff-messages">
            <div>
              <h3 className="messages-heading">Messages</h3>
              <p className="messages-subtitle">Recent submissions from the website contact form.</p>
            </div>
            {messages.length === 0 ? (
              <div className="message-empty">No messages have been received yet.</div>
            ) : (
              <div className="messages-grid">
                <div className="messages-inbox">
                  <div className="messages-inbox-header">
                    <div className="inbox-title">Inbox</div>
                    <button
                      type="button"
                      onClick={handleDeleteSelectedMessage}
                      disabled={!selectedMessageId}
                      className="delete-button"
                    >
                      Delete
                    </button>
                  </div>
                  <div className="messages-list">
                    {messages.map((msg) => (
                      <button
                        key={msg.id}
                        type="button"
                        onClick={() => setSelectedMessageId(msg.id)}
                        className={`message-item ${selectedMessageId === msg.id ? 'active' : ''}`}
                      >
                        <div className="message-sender">{msg.name}</div>
                        <div className="message-email">{msg.email}</div>
                        <div className="message-snippet">{msg.message}</div>
                        <div className="message-time">{new Date(msg.createdAt).toLocaleString()}</div>
                      </button>
                    ))}
                  </div>
                </div>
                <div className="message-view">
                  {messages.find((msg) => msg.id === selectedMessageId) ? (
                    (() => {
                      const selected = messages.find((msg) => msg.id === selectedMessageId);
                      return (
                        <>
                          <div className="message-view-header">
                            <div>
                              <div className="message-name">{selected.name}</div>
                              <a href={`mailto:${selected.email}`} className="message-email-link">{selected.email}</a>
                            </div>
                            <div className="message-time">{new Date(selected.createdAt).toLocaleString()}</div>
                          </div>
                          <div className="message-bubble">{selected.message}</div>
                          {selected.replies && selected.replies.length > 0 && (
                            <div className="reply-list">
                              {selected.replies.map((reply) => (
                                <div key={reply.id} className="reply-card">
                                  <div className="reply-meta">
                                    <div>{reply.sender}</div>
                                    <div>{new Date(reply.createdAt).toLocaleString()}</div>
                                  </div>
                                  <div className="reply-text">{reply.text}</div>
                                </div>
                              ))}
                            </div>
                          )}
                          <div className="reply-box">
                            <textarea
                              value={replyInputs}
                              onChange={(e) => handleReplyChange(e.target.value)}
                              placeholder="Write your reply..."
                              className="reply-textarea"
                            />
                            <button type="button" onClick={handleSubmitReply} className="reply-submit">
                              Send Reply
                            </button>
                          </div>
                        </>
                      );
                    })()
                  ) : (
                    <div className="message-view-empty">Select a message to view the conversation.</div>
                  )}
                </div>
              </div>
            )}
          </div>
        )}

        {activeTab === 'profile' && (
          <div className="tab-container admin-tab-panel admin-tab-panel--profile">
            <div className="account-settings-page">
              <div className="account-settings-header">
                <h1>{ts.myProfile}</h1>
                <p>{ts.profileDesc || 'Manage your account settings.'}</p>
              </div>
              <div className="crm-card account-settings-card">
                <h3 className="account-settings-card-title">{ts.myProfile}</h3>
                <div className="account-settings-field">
                  <label>{tc.email || 'Email Address'}</label>
                  <input type="text" value={currentUser?.email} disabled className="form-input" />
                </div>
                <div className="account-settings-field">
                  <label>{ts.corporateDesignation || 'Corporate Designation'}</label>
                  <select value={userPosition} onChange={(e) => setUserPosition(e.target.value)} className="form-input">
                  {role === 'HR' && (
                    <>
                      <option value="HR Manager">HR Manager</option>
                      <option value="HR Recruiter">HR Recruiter</option>
                      <option value="HR Intern">HR Intern</option>
                    </>
                  )}
                  {role === 'OPERATIONS' && (
                    <>
                      <option value="Operations Manager">Operations Manager</option>
                      <option value="Team Leader">Team Leader</option>
                    </>
                  )}
                  {role === 'CLIENT' && <option value="Client Partner">Client Partner</option>}
                  </select>
                </div>
                <button type="button" className="btn-action-approve account-settings-save">Save Profile</button>
              </div>
            </div>
          </div>
        )}
      </main>
    </div>
  );
};

export default StaffDashboard;